/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Cell, Grid, Heading, HFlow, Icon, Tag, Text, Tooltip, VFlow } from 'bold-ui'
import { AccordionCompositeItem } from 'components/accordion/AccordionCompositeItem'
import { ButtonLink, PrivateRoute } from 'components/route'
import theme from 'config/theme'
import { useFlags } from 'config/useFlagsContext'
import { TipoReceitaEnum, UnidadeMedidaTempoEnum } from 'graphql/types.generated'
import { Fragment } from 'react'
import { Switch, useRouteMatch } from 'react-router'
import { formatDate } from 'util/date/formatDate'
import { TipoReceitaTag } from 'view/atendimentos/detail/components/TipoReceitaTag'

import { grupoCboPrescricao } from '../../acessos'
import EncaminharPrescricaoModal from '../modal/EncaminhamentoPrescricaoModal'
import { PrescricaoDigitalFormModel } from '../modal/prescricao-digital/model-prescricaoDigital'
import { MedicamentoFormModel } from '../model-prescricao'
import {
  prescricaoAtendBodyConverter,
  prescricaoAtendHeaderConverter,
  PrescricaoAtendimentoBodyModel,
} from '../utils/converter-prescricao'
import DisponibilidadeHorus from './DisponibilidadeHorus'

interface AccordionPrescricaoProps {
  medicamentos: MedicamentoFormModel[]
  prescricaoDigital: PrescricaoDigitalFormModel
  updatePrescricaoDigital: (prescricao: PrescricaoDigitalFormModel) => void
  atendimentoId: ID
}

export default function AccordionPrescricao({
  medicamentos,
  atendimentoId,
  prescricaoDigital,
  updatePrescricaoDigital,
}: AccordionPrescricaoProps) {
  const match = useRouteMatch()
  const { MOTIVO_PRESCRICAO_ENABLED } = useFlags()

  const renderHeader = (title: string) => {
    return (
      <HFlow
        style={css`
          width: 100%;
        `}
      >
        <Heading level={5} style={styles.heading}>
          {title}
        </Heading>
        <div css={styles.adicionadoAgora}>
          <Icon
            icon='clockOutline'
            size={1}
            style={css`
              margin-right: 0.5rem;
            `}
          />
          Adicionado agora
        </div>
      </HFlow>
    )
  }

  const renderBody = (prescricaoAtendimento: PrescricaoAtendimentoBodyModel[]) => {
    return prescricaoAtendimento.map((medicamento) => {
      return (
        <div
          key={medicamento.id}
          css={css`
            padding: 0.5rem 1rem;
          `}
        >
          <Grid>
            <Cell size={8}>
              <HFlow alignItems='center' hSpacing={0.5}>
                <Heading
                  level={5}
                  style={css`
                    word-break: break-word;
                  `}
                >
                  {`${medicamento.principioAtivo} ${medicamento.concentracao ?? ''}`.trim()}
                </Heading>
                {medicamento.usoContinuo && <Tag type='normal'>Uso contínuo</Tag>}
                {medicamento.tipoReceita !== TipoReceitaEnum.COMUM && (
                  <TipoReceitaTag tipoReceita={medicamento.tipoReceita} />
                )}
              </HFlow>
              <Text>{`${medicamento.posologia} | ${medicamento.viaAdministracao.nome} | ${medicamento.formaFarmaceutica}`}</Text>
            </Cell>
            <Cell size={4}>
              <VFlow vSpacing={0}>
                <Heading level={5}>{`Período: ${formatDate(medicamento.dataInicioTratamento)} 
                - ${
                  medicamento.escalaDuracao !== UnidadeMedidaTempoEnum.INDETERMINADO
                    ? formatDate(medicamento.dataFimTratamento)
                    : 'Indeterminado'
                }
              `}</Heading>
                <Text>
                  <DisponibilidadeHorus disponibilidade={medicamento.disponibilidade} />
                </Text>
              </VFlow>
            </Cell>
            <Cell size={12}>
              <Heading level={5}>Recomendações</Heading>
              {medicamento.recomendacoes ? (
                <Text
                  style={css`
                    white-space: pre-wrap;
                    overflow-wrap: anywhere;
                  `}
                >
                  {medicamento.recomendacoes}
                </Text>
              ) : (
                <Text fontStyle='italic'>Não há recomendações para o medicamento</Text>
              )}
            </Cell>
            {MOTIVO_PRESCRICAO_ENABLED && (
              <Cell size={12}>
                <Heading level={5}>Motivo da prescrição</Heading>
                <Text>{medicamento.motivoPrescricao}</Text>
              </Cell>
            )}
          </Grid>
        </div>
      )
    })
  }

  const renderHeaderButtons = () => {
    return (
      <HFlow hSpacing={0} alignItems='center'>
        <Tooltip text='Encaminhar'>
          <ButtonLink size='small' skin='ghost' to={`${match.url}/prescricao-medicamentos/encaminhar`}>
            <Icon icon='printerOutline' />
          </ButtonLink>
        </Tooltip>
      </HFlow>
    )
  }

  return (
    <Fragment>
      <AccordionCompositeItem
        header={renderHeader(prescricaoAtendHeaderConverter(medicamentos))}
        headerButtons={renderHeaderButtons()}
        body={renderBody(prescricaoAtendBodyConverter(medicamentos))}
        dataTestid='AccordionCompositeItem.Medicamento'
      />
      <Switch>
        <PrivateRoute
          path={`${match.url}/prescricao-medicamentos/encaminhar`}
          permission={grupoCboPrescricao.adicionar}
        >
          <EncaminharPrescricaoModal
            isReimpressao={false}
            medicamentos={medicamentos}
            prescricaoDigital={prescricaoDigital}
            updatePrescricaoDigital={updatePrescricaoDigital}
            atendimentoId={atendimentoId}
          />
        </PrivateRoute>
      </Switch>
    </Fragment>
  )
}

const styles = {
  heading: css`
    display: flex;
    align-items: center;
  `,
  adicionadoAgora: css`
    color: ${theme.pallete.primary.main};
    display: flex;
    align-items: center;
  `,
}
